/**
 * GlobalState.js
 * Description: provides state/data for the whole app
 */

import { createContext } from "react";
import { tokenize } from "../analyzer/lexer";
import { parser } from "../analyzer/parser";
import { semantiker } from "../analyzer/semantiker";

/**
 * Context API Concepts
 * 1. Provider -> provides the global state to its children components
 * 2. Consumer -> component that indicates where you want to access the global state
 * 3. Reducer -> function that reduces/updates the global state
 *    - Dispatch -> function that allows to update the global state
 *    - Payload -> string argument of Dispatch used to update the global state
 */

const initialState = {
  sourceCode: "", //code that is ready for execution
  editorCode: "", //code that is saved before execution
  tokens: [],
  outputMessage: "",
  syntaxTree: {},
  symbolTable: {},
  filename: "",
};

export const reducer = (state, action) => {
  try {
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
      case "SET_SOURCE_CODE":
        const { tokens } = tokenize(payload);
        const syntaxTree = parser(tokens);
        const { symbolTable, outputMessages } = semantiker(syntaxTree);
        const joinedMessage = outputMessages.join("\n");

        return {
          ...state,
          tokens: tokens,
          syntaxTree: syntaxTree,
          symbolTable: symbolTable,
          outputMessage: joinedMessage,
        };
      case "SET_EDITOR_CODE":
        return {
          ...state,
          editorCode: payload,
        };

      case "SET_OUTPUT_MESSAGE":
        return {
          ...state,
          outputMessage: payload,
        };
      case "SET_FILENAME": {
        return {
          ...state,
          filename: payload,
        };
      }
    }
  } catch (err) {
    return {
      ...state,
      outputMessage: err.message,
      syntaxTree: {},
      tokens: [],
    };
  }
};

const StateContext = createContext({ state: initialState, dispatch: () => 0 });

export const Provider = StateContext.Provider;

export const context = StateContext;

export const defaultState = initialState;
